// NeuAnlageDialog.tsx — die Vorfrage vor dem Anlegen aus dem „+“-Menü.
//
// Fragt nur das Nötige ab und springt dann in den VORHANDENEN Anlegeweg:
//  - Risiko   → Kontakt, Risikoart          → Risiko-Erfassungsfenster
//  - Vertrag  → Kontakt, Vertragsart (+ Immobilien-Risikoobjekt)
//                                           → passende Vertragsmaske
//  - Schaden  → Kontakt                     → Schaden-Erfassungsfenster
//  - Dokument → Art (Word | Excel | Datei hochladen), Kontakt, „Für einen
//               Vorgang?" (Nein | Ja → Vorgangsauswahl), dann Name bzw.
//               Kategorie + Datei(en)
//                                           → leeres Word/Excel im Editor
//                                             bzw. Upload in die Kundenakte
//
// Schale: `.kvp-rd.kvp-rd-scrim > .dm` (redesign/contactRedesign.css), per
// Portal an `body`. Esc und Klick daneben schließen; auf Handybreite ein
// Blatt von unten (neuAnlage.css).
//
// Alles, was Fenster öffnet, navigiert oder das Netz braucht, kommt über
// `aktionen` herein — der Dialog selbst entscheidet nur, WAS abgefragt wird.
//
// `vorbelegung` (T-230): Kontakt und — beim Dokument — Vorgang der Seite, auf
// der das „+“ gedrückt wurde (siehe vorbelegung.ts). Der Dialog startet damit,
// jeder Schritt bleibt änderbar.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import {
  ArrowLeft20Regular,
  Checkmark24Regular,
  ChevronRight16Regular,
  Clipboard20Regular,
  Dismiss20Regular,
  Document20Regular,
  Home20Regular,
  Search20Regular,
  Shield20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import { VERTRAGSKATALOG, type Vertragsart, type Vertragskategorie } from "../../data/vertragskatalog";
import { DOKUMENT_KATEGORIEN } from "../../data/dokumentKategorien";
import { RISIKO_ARTEN, type NewRiskType } from "../risikoArten";
import { KontaktSuche, type KontaktSucheFn, type KontaktTreffer } from "./KontaktSuche";
import { DATEI_HOCHLADEN, OfficeArtKacheln, OfficeNameFeld, type DokumentWeg } from "./OfficeArtUndName";
import { OfficeOpenDialog } from '../documents/OfficeOpenDialog';
import { nameGueltig, type OfficeArt } from "./officeDokument";
import { vertragsZiel, type VertragsZiel } from "./vertragsMaske";
import "../redesign/contactRedesign.css";
import "./neuAnlage.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export type VorfrageArt = "risiko" | "vertrag" | "schaden" | "dokument";

export type ImmobilienRisiko = { id: number; name: string; adresse?: string | null };

/** Ein Vorgang des gewählten Kontakts, wie ihn die Auswahl zeigt. */
export type VorgangAuswahl = { id: number; titel: string; nummer?: string | null; offen: boolean };

/** Womit der Dialog startet (T-230). Der Vorgang gehört zum Kontakt. */
export type NeuAnlageVorbelegung = { kontakt: KontaktTreffer; vorgang?: VorgangAuswahl | null };

/** Vorgänge des Kontakts: offene zuerst, darin die neuesten oben. */
export const vorgaengeFuerAuswahl = (
  liste: Array<{ id: number; titel?: string | null; vorgangNbr?: string | null; status?: string; createdAt?: string }>,
): VorgangAuswahl[] =>
  liste
    .map((v) => ({
      id: v.id,
      titel: (v.titel ?? "").trim(),
      nummer: v.vorgangNbr ?? null,
      // „Erledigt" ist der einzige geschlossene Status (deriveVorgangStatus).
      offen: v.status !== "Erledigt",
      zeit: v.createdAt ? Date.parse(v.createdAt) || 0 : 0,
    }))
    .sort((a, b) => Number(b.offen) - Number(a.offen) || b.zeit - a.zeit || b.id - a.id)
    .map(({ zeit: _zeit, ...v }) => v);

export type NeuAnlageAktionen = {
  kontaktSuchen: KontaktSucheFn;
  risikoAnlegen: (p: { riskType: NewRiskType; kontakt: KontaktTreffer }) => void;
  vertragAnlegen: (p: {
    ziel: Exclude<VertragsZiel, { typ: "immobilie-risiko-waehlen" }>;
    kontakt: KontaktTreffer;
  }) => void;
  immobilienRisikenLaden: (kontaktId: number) => Promise<ImmobilienRisiko[]>;
  immobilienVertragAnlegen: (p: { kontakt: KontaktTreffer; riskObjectId: number }) => void;
  schadenAnlegen: (p: { kontakt: KontaktTreffer }) => void;
  /** Lädt EINE Datei hoch und liefert die neue Dokument-Id. */
  dokumentHochladen: (p: {
    datei: File;
    kontaktId: number;
    kategorie: string | null;
    vorgangId: number | null;
  }) => Promise<number>;
  /** Vorgänge des Kontakts für „Für einen Vorgang? → Ja". */
  vorgaengeLaden: (kontaktId: number) => Promise<VorgangAuswahl[]>;
  /** Leeres Word/Excel anlegen, liefert die neue Dokument-Id. */
  officeAnlegen: (p: { art: OfficeArt; name: string; kontaktId: number; vorgangId: number | null }) => Promise<number>;
  /** Tab im Klick öffnen (Popup-Sperre), nach dem Anlegen umlenken bzw. verwerfen. */
  editorTabVorbereiten: () => Window | null;
  editorOeffnen: (dokumentId: number, tab: Window | null) => void;
  editorTabVerwerfen: (tab: Window | null) => void;
  kontaktOeffnen: (kontaktId: number, dokumentId?: number) => void;
};

const TITEL: Record<VorfrageArt, string> = {
  risiko: "Neues Risiko",
  vertrag: "Neuer Vertrag",
  schaden: "Neuer Schaden",
  dokument: "Neues Dokument",
};

const SYMBOL: Record<VorfrageArt, React.ReactElement> = {
  risiko: <Shield20Regular />,
  vertrag: <Clipboard20Regular />,
  schaden: <Warning20Regular />,
  dokument: <Document20Regular />,
};

/** Die Risikoarten, die die Erfassung heute schon kann („folgt" bleibt weg). */
const WAEHLBARE_RISIKOARTEN = RISIKO_ARTEN.filter((t) => !t.comingSoon);

/** „Ohne Kategorie" — in der Kundenakte ist das der Normalfall. */
const SONSTIGES = "";

const groesse = (b: number): string =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const normal = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/* ── Kleinteile ───────────────────────────────────────────────────────── */

function Schritte({ namen, aktuell }: { namen: string[]; aktuell: number }) {
  if (namen.length < 2) return null;
  return (
    <ol className="na-schritte" aria-label="Schritte" style={{ listStyle: "none", padding: 0 }}>
      {namen.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && <span className="na-schritt-trenner" aria-hidden />}
          <li
            className={"na-schritt" + (i === aktuell ? " an" : i < aktuell ? " fertig" : "")}
            aria-current={i === aktuell ? "step" : undefined}
          >
            <span className="na-schritt-nr">{i + 1}</span>
            {n}
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
}

/* ── Dialog ───────────────────────────────────────────────────────────── */

export function NeuAnlageDialog({
  art,
  aktionen,
  onClose,
  officeVerfuegbar = true,
  vorbelegung = null,
}: {
  art: VorfrageArt;
  aktionen: NeuAnlageAktionen;
  onClose: () => void;
  /** Gibt es den Office-Editor? Ohne ihn bleibt beim Dokument nur „Datei hochladen". */
  officeVerfuegbar?: boolean;
  /** Kontakt/Vorgang der aktuellen Seite (T-230) — nur der Startwert. */
  vorbelegung?: NeuAnlageVorbelegung | null;
}) {
  const [kontakt, setKontakt] = React.useState<KontaktTreffer | null>(vorbelegung?.kontakt ?? null);
  // Der vorbelegte Vorgang zählt nur, solange sein Kontakt gewählt ist.
  const vorbelegterVorgang = (vorbelegung?.kontakt && vorbelegung.vorgang) || null;

  // Vertrag
  const [vertragsSuche, setVertragsSuche] = React.useState("");
  const [kategorie, setKategorie] = React.useState<Vertragskategorie | null>(null);
  const [immoArt, setImmoArt] = React.useState<{ art: Vertragsart; kategorieId: string | null } | null>(null);
  const [immoRisiken, setImmoRisiken] = React.useState<ImmobilienRisiko[] | null>(null);

  // Dokument
  const [dokWeg, setDokWeg] = React.useState<DokumentWeg | null>(officeVerfuegbar ? null : DATEI_HOCHLADEN);
  const [fuerVorgang, setFuerVorgang] = React.useState<boolean | null>(vorbelegterVorgang ? true : null);
  const [vorgaenge, setVorgaenge] = React.useState<VorgangAuswahl[] | null>(null);
  const [vorgangId, setVorgangId] = React.useState<number | null>(vorbelegterVorgang?.id ?? null);
  const [dokName, setDokName] = React.useState("");
  const [officeAuswahl, setOfficeAuswahl] = React.useState(false);
  const [legtAn, setLegtAn] = React.useState(false);
  const [dokArt, setDokArt] = React.useState<string>(SONSTIGES);
  const [dateien, setDateien] = React.useState<File[]>([]);
  const [ziehen, setZiehen] = React.useState(false);
  const [laedtHoch, setLaedtHoch] = React.useState(false);
  const [fehler, setFehler] = React.useState("");
  const [fertig, setFertig] = React.useState<{ anzahl: number; ersteId: number } | null>(null);
  const dateiInput = React.useRef<HTMLInputElement>(null);

  const schliessbar = !laedtHoch && !legtAn && !officeAuswahl;
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && schliessbar) onCloseRef.current();
    };
    alleFenster.addEventListener("keydown", esc);
    return () => alleFenster.removeEventListener("keydown", esc);
  }, [schliessbar]);

  // Immobilien-Risikoobjekte des Kontakts nachladen, sobald sie gebraucht werden.
  React.useEffect(() => {
    if (!immoArt || !kontakt) return;
    let weg = false;
    setImmoRisiken(null);
    aktionen
      .immobilienRisikenLaden(kontakt.id)
      .then((l) => !weg && setImmoRisiken(l))
      .catch(() => !weg && setImmoRisiken([]));
    return () => {
      weg = true;
    };
  }, [immoArt, kontakt, aktionen]);

  // Vorgänge des Kontakts erst laden, wenn „Ja, für einen Vorgang" gewählt ist.
  React.useEffect(() => {
    if (fuerVorgang !== true || !kontakt) return;
    let weg = false;
    setVorgaenge(null);
    // Der vorbelegte Vorgang steht immer in der Liste — auch wenn die Liste
    // des Kontakts ihn (noch) nicht führt, sonst wäre er gewählt, aber unsichtbar.
    const vorbelegt = vorbelegterVorgang && vorbelegung?.kontakt.id === kontakt.id ? vorbelegterVorgang : null;
    const mitVorbelegtem = (l: VorgangAuswahl[]) =>
      vorbelegt && !l.some((v) => v.id === vorbelegt.id) ? [vorbelegt, ...l] : l;
    aktionen
      .vorgaengeLaden(kontakt.id)
      .then((l) => !weg && setVorgaenge(mitVorbelegtem(l)))
      .catch(() => !weg && setVorgaenge(mitVorbelegtem([])));
    return () => {
      weg = true;
    };
    // `vorbelegung` ist nur der Startwert und ändert sich im offenen Dialog nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuerVorgang, kontakt, aktionen]);

  const kontaktWaehlen = (k: KontaktTreffer | null) => {
    setKontakt(k);
    // Die Vorgangsfrage gilt nur für DIESEN Kontakt.
    setFuerVorgang(null);
    setVorgangId(null);
    setVorgaenge(null);
    if (!k) {
      setKategorie(null);
      setImmoArt(null);
    }
  };

  /* ── Vertrag: nach der Vertragsart ── */
  const vertragsartWaehlen = (a: Vertragsart | null, kategorieId: string | null) => {
    if (!kontakt) return;
    const ziel = vertragsZiel(a, kategorieId, kontakt.id);
    if (ziel.typ === "immobilie-risiko-waehlen") {
      if (a) setImmoArt({ art: a, kategorieId });
      return;
    }
    onClose();
    aktionen.vertragAnlegen({ ziel, kontakt });
  };

  const trefferArten = React.useMemo(() => {
    const q = normal(vertragsSuche.trim());
    if (!q) return [];
    const out: { art: Vertragsart; kat: Vertragskategorie }[] = [];
    for (const kat of VERTRAGSKATALOG)
      for (const a of kat.arten) if (normal(a.name).includes(q)) out.push({ art: a, kat });
    return out.slice(0, 40);
  }, [vertragsSuche]);

  /* ── Dokument ── */
  const dateienNehmen = (liste: FileList | null) => {
    if (!liste || !liste.length) return;
    setFehler("");
    setDateien((alt) => [...alt, ...Array.from(liste)]);
  };

  const hochladen = async () => {
    if (!kontakt || !dateien.length) return;
    setLaedtHoch(true);
    setFehler("");
    const ids: number[] = [];
    try {
      for (const d of dateien) {
        ids.push(
          await aktionen.dokumentHochladen({
            datei: d,
            kontaktId: kontakt.id,
            kategorie: dokArt || null,
            vorgangId: fuerVorgang ? vorgangId : null,
          }),
        );
      }
      setFertig({ anzahl: ids.length, ersteId: ids[0] });
    } catch (e) {
      const text = e instanceof Error && e.message ? e.message : "Hochladen fehlgeschlagen.";
      // Bereits hochgeladene nicht ein zweites Mal schicken.
      setDateien((alt) => alt.slice(ids.length));
      setFehler(ids.length ? `${ids.length} hochgeladen, dann: ${text}` : text);
    } finally {
      setLaedtHoch(false);
    }
  };

  /** Word/Excel: anlegen und im Editor öffnen. Der Tab entsteht noch im Klick. */
  const officeArt: OfficeArt | null = dokWeg && dokWeg !== DATEI_HOCHLADEN ? dokWeg : null;
  const vorgangGeklaert = fuerVorgang === false || (fuerVorgang === true && vorgangId !== null);
  const officeBereit = !!officeArt && !!kontakt && vorgangGeklaert && nameGueltig(dokName, officeArt) && !legtAn;
  const officeAnlegen = () => {if(officeBereit) setOfficeAuswahl(true);};

  /* ── Schritte ── */
  let schrittNamen: string[] = [];
  let schritt = 0;
  if (art === "risiko") {
    schrittNamen = ["Kontakt", "Risikoart"];
    schritt = kontakt ? 1 : 0;
  } else if (art === "vertrag") {
    schrittNamen = immoArt ? ["Kontakt", "Vertragsart", "Risikoobjekt"] : ["Kontakt", "Vertragsart"];
    schritt = !kontakt ? 0 : immoArt ? 2 : 1;
  } else if (art === "dokument" && !fertig) {
    const letzter = dokWeg === DATEI_HOCHLADEN ? "Datei" : "Name";
    schrittNamen = officeVerfuegbar ? ["Art", "Kontakt", "Vorgang", letzter] : ["Kontakt", "Vorgang", letzter];
    const i = !dokWeg ? 0 : !kontakt ? 1 : !vorgangGeklaert ? 2 : 3;
    schritt = officeVerfuegbar ? i : Math.max(0, i - 1);
  }

  /* ── Inhalt ── */
  let inhalt: React.ReactNode;
  let fuss: React.ReactNode = null;

  const kontaktAbschnitt = (
    <section className="na-abschnitt">
      {!kontakt && <h3 className="na-frage">Für welchen Kontakt?</h3>}
      <KontaktSuche gewaehlt={kontakt} onWaehlen={kontaktWaehlen} suchen={aktionen.kontaktSuchen} autoFocus={!kontakt} />
    </section>
  );

  if (art === "risiko") {
    inhalt = (
      <>
        {kontaktAbschnitt}
        {kontakt && (
          <section className="na-abschnitt">
            <h3 className="na-frage">Welche Art von Risiko?</h3>
            <div className="na-kacheln">
              {WAEHLBARE_RISIKOARTEN.map((t) => {
                const Icon = t.Icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    className="na-kachel"
                    onClick={() => {
                      onClose();
                      aktionen.risikoAnlegen({ riskType: t.key, kontakt });
                    }}
                  >
                    <span className="na-kachel-ico" aria-hidden>
                      <Icon />
                    </span>
                    <span className="na-kachel-text">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </>
    );
  } else if (art === "vertrag") {
    let vertragsTeil: React.ReactNode = null;
    if (kontakt && immoArt) {
      vertragsTeil = (
        <section className="na-abschnitt">
          <h3 className="na-frage">Für welche Immobilie?</h3>
          <p className="na-hinweis">
            {immoArt.art.name} hängt an einem Immobilien-Risikoobjekt von {kontakt.name}.
          </p>
          {immoRisiken === null ? (
            <div className="na-leise">Lädt …</div>
          ) : (
            <div className="na-liste">
              {immoRisiken.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="na-listen-eintrag"
                  onClick={() => {
                    onClose();
                    aktionen.immobilienVertragAnlegen({ kontakt, riskObjectId: r.id });
                  }}
                >
                  <span>
                    {r.name}
                    {r.adresse ? <span className="na-listen-sub">{r.adresse}</span> : null}
                  </span>
                  <ChevronRight16Regular className="na-listen-pfeil" />
                </button>
              ))}
              {immoRisiken.length === 0 && <div className="na-leise">Dieser Kontakt hat noch keine Immobilie.</div>}
              <button
                type="button"
                className="na-listen-eintrag leise"
                onClick={() => {
                  onClose();
                  aktionen.risikoAnlegen({ riskType: "immobilie", kontakt });
                }}
              >
                <span>
                  <Home20Regular style={{ verticalAlign: "-4px", marginRight: 8 }} />
                  Neue Immobilie als Risiko anlegen
                </span>
                <ChevronRight16Regular className="na-listen-pfeil" />
              </button>
            </div>
          )}
        </section>
      );
    } else if (kontakt) {
      vertragsTeil = (
        <section className="na-abschnitt">
          <h3 className="na-frage">Welche Vertragsart?</h3>
          <div className="na-suche" style={{ marginBottom: 12 }}>
            <Search20Regular aria-hidden />
            <input
              type="text"
              aria-label="Vertragsart suchen"
              placeholder="Vertragsart suchen, z. B. Betriebshaftpflicht"
              value={vertragsSuche}
              onChange={(e) => setVertragsSuche(e.target.value)}
              autoFocus
            />
          </div>
          {vertragsSuche.trim() ? (
            <div className="na-liste">
              {trefferArten.map(({ art: a, kat }) => (
                <button key={a.id} type="button" className="na-listen-eintrag" onClick={() => vertragsartWaehlen(a, kat.id)}>
                  <span>
                    {a.name}
                    <span className="na-listen-sub">{kat.name}</span>
                  </span>
                  <ChevronRight16Regular className="na-listen-pfeil" />
                </button>
              ))}
              {trefferArten.length === 0 && <div className="na-leise">Keine Vertragsart gefunden.</div>}
            </div>
          ) : kategorie ? (
            <div className="na-liste">
              <button type="button" className="na-link" style={{ alignSelf: "flex-start" }} onClick={() => setKategorie(null)}>
                ← Alle Sparten
              </button>
              <div className="na-gewaehlt-label" style={{ margin: "4px 2px 2px" }}>
                {kategorie.name}
              </div>
              {kategorie.arten.map((a) => (
                <button key={a.id} type="button" className="na-listen-eintrag" onClick={() => vertragsartWaehlen(a, kategorie.id)}>
                  <span>{a.name}</span>
                  <ChevronRight16Regular className="na-listen-pfeil" />
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="na-kacheln">
                {VERTRAGSKATALOG.map((k) => (
                  <button key={k.id} type="button" className="na-kachel" onClick={() => setKategorie(k)}>
                    <span className="na-kachel-text">
                      {k.name}
                      <span className="na-kachel-sub">
                        {k.arten.length} {k.arten.length === 1 ? "Vertragsart" : "Vertragsarten"}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="na-liste" style={{ marginTop: 10 }}>
                <button type="button" className="na-listen-eintrag leise" onClick={() => vertragsartWaehlen(null, null)}>
                  <span>
                    Allgemeiner Vertrag
                    <span className="na-listen-sub">Vertragsart erst in der Maske wählen</span>
                  </span>
                  <ChevronRight16Regular className="na-listen-pfeil" />
                </button>
              </div>
            </>
          )}
        </section>
      );
    }
    inhalt = (
      <>
        {kontaktAbschnitt}
        {vertragsTeil}
      </>
    );
    if (immoArt) {
      fuss = (
        <button type="button" className="na-btn" onClick={() => setImmoArt(null)}>
          <ArrowLeft20Regular /> Andere Vertragsart
        </button>
      );
    }
  } else if (art === "schaden") {
    inhalt = (
      <>
        {kontaktAbschnitt}
        {kontakt && (
          <p className="na-hinweis" style={{ marginTop: 14 }}>
            Risiko, Zeitpunkt und Hergang fragt die Schadenerfassung als Nächstes ab.
          </p>
        )}
      </>
    );
    fuss = (
      <button
        type="button"
        className="na-btn primaer"
        disabled={!kontakt}
        onClick={() => {
          if (!kontakt) return;
          onClose();
          aktionen.schadenAnlegen({ kontakt });
        }}
      >
        Schaden erfassen
      </button>
    );
  } else if (fertig && kontakt) {
    inhalt = (
      <div className="na-erfolg" role="status">
        <span className="na-erfolg-ico" aria-hidden>
          <Checkmark24Regular />
        </span>
        <strong>{fertig.anzahl === 1 ? "Dokument abgelegt" : `${fertig.anzahl} Dokumente abgelegt`}</strong>
        <span>
          in der Kundenakte von {kontakt.name}
          {dokArt ? ` · ${dokArt}` : ""}
          {fuerVorgang && vorgangId !== null ? " · am gewählten Vorgang" : ""}
        </span>
      </div>
    );
    fuss = (
      <>
        <button
          type="button"
          className="na-btn"
          onClick={() => {
            onClose();
            aktionen.kontaktOeffnen(kontakt.id, fertig.ersteId);
          }}
        >
          Zum Kontakt
        </button>
        <button type="button" className="na-btn primaer" onClick={onClose} autoFocus>
          Fertig
        </button>
      </>
    );
  } else {
    // Dokument
    const gewaehlterVorgang = vorgaenge?.find((v) => v.id === vorgangId) ?? null;
    inhalt = (
      <>
        {officeVerfuegbar && (
          <section className="na-abschnitt">
            <h3 className="na-frage">Welche Art von Dokument?</h3>
            <OfficeArtKacheln gewaehlt={dokWeg} onWaehlen={setDokWeg} mitHochladen />
          </section>
        )}
        {dokWeg && (
          <section className="na-abschnitt">
            {!kontakt && <h3 className="na-frage">Für welchen Kontakt?</h3>}
            <KontaktSuche
              gewaehlt={kontakt}
              onWaehlen={kontaktWaehlen}
              suchen={aktionen.kontaktSuchen}
              autoFocus={!kontakt}
            />
          </section>
        )}
        {dokWeg && kontakt && (
          <section className="na-abschnitt">
            <h3 className="na-frage">Für einen Vorgang?</h3>
            <div className="na-kacheln" role="radiogroup" aria-label="Für einen Vorgang?">
              {[
                { wert: false, label: "Nein", sub: "Nur in die Kundenakte" },
                { wert: true, label: "Ja", sub: `Einen Vorgang von ${kontakt.name} wählen` },
              ].map((o) => (
                <button
                  key={String(o.wert)}
                  type="button"
                  role="radio"
                  aria-checked={fuerVorgang === o.wert}
                  className={"na-kachel" + (fuerVorgang === o.wert ? " an" : "")}
                  onClick={() => {
                    setFuerVorgang(o.wert);
                    if (!o.wert) setVorgangId(null);
                  }}
                >
                  <span className="na-kachel-text">
                    {o.label}
                    <span className="na-kachel-sub">{o.sub}</span>
                  </span>
                </button>
              ))}
            </div>
            {fuerVorgang === true && (
              <div style={{ marginTop: 12 }}>
                {vorgaenge === null ? (
                  <div className="na-leise">Vorgänge werden geladen …</div>
                ) : vorgaenge.length === 0 ? (
                  <div className="na-leise">{kontakt.name} hat noch keinen Vorgang.</div>
                ) : (
                  <div className="na-liste na-vorgaenge" role="radiogroup" aria-label="Vorgang">
                    {vorgaenge.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        role="radio"
                        aria-checked={vorgangId === v.id}
                        className="na-listen-eintrag"
                        onClick={() => setVorgangId(v.id)}
                      >
                        <span style={{ minWidth: 0 }}>
                          {v.titel || `Vorgang ${v.nummer ?? v.id}`}
                          {v.nummer ? <span className="na-listen-sub">{v.nummer}</span> : null}
                        </span>
                        <span className={"na-status" + (v.offen ? "" : " zu")}>{v.offen ? "offen" : "erledigt"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
        {officeArt && kontakt && vorgangGeklaert && (
          <section className="na-abschnitt">
            <h3 className="na-frage">
              Name {officeArt === "docx" ? "des Word-Dokuments" : "der Excel-Tabelle"}
            </h3>
            <OfficeNameFeld
              key={officeArt}
              art={officeArt}
              name={dokName}
              onName={setDokName}
              onEnter={() => void officeAnlegen()}
              autoFocus
            />
            {fehler && (
              <div className="na-fehler" role="alert">
                {fehler}
              </div>
            )}
          </section>
        )}
        {dokWeg === DATEI_HOCHLADEN && kontakt && vorgangGeklaert && (
          <>
            <section className="na-abschnitt">
              <h3 className="na-frage">Welche Kategorie?</h3>
              <div className="na-kacheln" role="radiogroup" aria-label="Kategorie">
                {[{ value: SONSTIGES, hinweis: "Ohne besondere Kategorie" }, ...DOKUMENT_KATEGORIEN].map((k) => (
                  <button
                    key={k.value || "sonstiges"}
                    type="button"
                    role="radio"
                    aria-checked={dokArt === k.value}
                    className={"na-kachel" + (dokArt === k.value ? " an" : "")}
                    onClick={() => setDokArt(k.value)}
                  >
                    <span className="na-kachel-text">
                      {k.value || "Sonstiges"}
                      {k.hinweis ? <span className="na-kachel-sub">{k.hinweis}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            </section>
            <section className="na-abschnitt">
              <h3 className="na-frage">Datei</h3>
              <div
                className={"na-ablage" + (ziehen ? " ziehen" : "")}
                role="button"
                tabIndex={0}
                aria-label="Datei auswählen"
                onClick={() => dateiInput.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    dateiInput.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setZiehen(true);
                }}
                onDragLeave={() => setZiehen(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setZiehen(false);
                  dateienNehmen(e.dataTransfer.files);
                }}
              >
                <Document20Regular aria-hidden />
                Datei hierher ziehen oder auswählen
                <small>Mehrere Dateien möglich</small>
              </div>
              <input
                ref={dateiInput}
                type="file"
                multiple
                hidden
                data-testid="na-datei-input"
                onChange={(e) => {
                  dateienNehmen(e.target.files);
                  e.target.value = "";
                }}
              />
              {dateien.length > 0 && (
                <ul className="na-dateien">
                  {dateien.map((d, i) => (
                    <li key={`${d.name}-${i}`} className="na-datei">
                      <Document20Regular aria-hidden />
                      <span className="na-datei-name">{d.name}</span>
                      <span className="na-datei-groesse">{groesse(d.size)}</span>
                      <button
                        type="button"
                        className="na-datei-weg"
                        aria-label={`${d.name} entfernen`}
                        disabled={laedtHoch}
                        onClick={() => setDateien((alt) => alt.filter((_, j) => j !== i))}
                      >
                        <Dismiss20Regular />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fehler && (
                <div className="na-fehler" role="alert">
                  {fehler}
                </div>
              )}
            </section>
          </>
        )}
        {gewaehlterVorgang && vorgangGeklaert && (
          <p className="na-hinweis" style={{ marginTop: 14 }}>
            Das Dokument hängt danach am Vorgang „{gewaehlterVorgang.titel}“ und liegt in der Kundenakte.
          </p>
        )}
      </>
    );
    if (officeArt) {
      fuss = (
        <button type="button" className="na-btn primaer" disabled={!officeBereit} onClick={() => void officeAnlegen()}>
          {legtAn ? "Wird angelegt …" : "Anlegen und öffnen"}
        </button>
      );
    } else if (dokWeg === DATEI_HOCHLADEN) {
      fuss = (
        <button
          type="button"
          className="na-btn primaer"
          disabled={!kontakt || !vorgangGeklaert || !dateien.length || laedtHoch}
          onClick={() => void hochladen()}
        >
          {laedtHoch ? "Wird hochgeladen …" : dateien.length > 1 ? `${dateien.length} Dateien hochladen` : "Hochladen"}
        </button>
      );
    }
  }

  if(officeAuswahl && officeArt && kontakt) return <OfficeOpenDialog
    createDocument={()=>aktionen.officeAnlegen({art:officeArt,name:dokName.trim(),kontaktId:kontakt.id,vorgangId:fuerVorgang?vorgangId:null})}
    prepareTab={aktionen.editorTabVorbereiten}
    discardTab={aktionen.editorTabVerwerfen}
    openOnlyOffice={aktionen.editorOeffnen}
    onOpened={()=>{}}
    onDismiss={()=>{setOfficeAuswahl(false);onClose();}}
  />;

  return createPortal(
    <div
      className="kvp-rd kvp-rd-scrim na-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && schliessbar) onClose();
      }}
    >
      <div className="dm na-dm" role="dialog" aria-modal="true" aria-label={TITEL[art]}>
        <div className="dm-head">
          <div className="dm-head-top">
            <span className="dm-ico" aria-hidden>
              {SYMBOL[art]}
            </span>
            <div className="dm-titles">
              <div className="dm-kicker">Neu anlegen</div>
              <h2>{TITEL[art]}</h2>
            </div>
            <button type="button" className="dm-x" aria-label="Schließen" onClick={onClose} disabled={!schliessbar}>
              <Dismiss20Regular />
            </button>
          </div>
          <Schritte namen={schrittNamen} aktuell={schritt} />
        </div>
        <div className="dm-body">{inhalt}</div>
        <div className="dm-foot na-foot">
          {fertig ? null : (
            <button type="button" className="na-btn" onClick={onClose} disabled={!schliessbar}>
              Abbrechen
            </button>
          )}
          <span style={{ flex: 1 }} />
          {fuss}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default NeuAnlageDialog;
